import { FC } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { FieldPath, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Content } from '@/components/layout/Content';
import { NotFound } from '@/components/NotFound';
import { LoadingPage } from '@/components/LoadingPage';
import { useQuery } from '@/hooks/graphql/useQuery';
import { useMutation } from '@/hooks/graphql/useMutation';
import { useNotify } from '@/hooks/useNotify';
import { eachValidationErrorsForHookFrom, ServerValidationError } from '@/hooks/graphql/error/ServerValidationError';
import { parseBackUrl } from '@/utils/url';
import { Form } from './parts/Form/Form';
import {
  REGULAR_HEALTH_CHECKUP_RESULT,
  RegularHealthCheckupResultQueryVariables,
  RegularHealthCheckupResultQueryResult,
  UPDATE_REGULAR_HEALTH_CHECKUP_RESULT,
  UpdateRegularHealthCheckupResultQueryVariables,
  UpdateRegularHealthCheckupResultQueryResult,
  convertFormDataToQueryVariables,
  generateDefaultValues
} from './query';
import { Schema, schema } from './schema';

interface SearchParams {
  personalId: string;
  resultId: string;
}

export const EditPersonalResult: FC = () => {
  const { personalId, resultId } = useParams<SearchParams>();

  const { data, loading } = useQuery<RegularHealthCheckupResultQueryResult, RegularHealthCheckupResultQueryVariables>(
    REGULAR_HEALTH_CHECKUP_RESULT,
    {
      variables: { personalId, resultId },
      fetchPolicy: 'network-only'
    }
  );
  const regularHealthCheckupResult = data?.client?.regularHealthCheckupResult;

  if (!loading && !regularHealthCheckupResult) {
    return <NotFound />;
  }

  return (
    <>
      <div className="l-title-wrap">
        <h1 className="m-title-main">個人結果</h1>
      </div>
      <Content size="xl">
        {loading ? (
          <LoadingPage />
        ) : (
          <FormWrapper
            personalId={personalId}
            resultId={resultId}
            defaultValues={generateDefaultValues(regularHealthCheckupResult)}
          />
        )}
      </Content>
    </>
  );
};

interface FormWrapperParams {
  personalId: string;
  resultId: string;
  defaultValues: Schema;
}

const FormWrapper: FC<FormWrapperParams> = ({ personalId, resultId, defaultValues }) => {
  const location = useLocation();
  const history = useHistory();
  const notify = useNotify();

  const methods = useForm<Schema>({
    defaultValues,
    resolver: yupResolver(schema)
  });
  const { handleSubmit, setError } = methods;

  const [updateRegularHealthCheckupResult] = useMutation<
    UpdateRegularHealthCheckupResultQueryResult,
    UpdateRegularHealthCheckupResultQueryVariables
  >(UPDATE_REGULAR_HEALTH_CHECKUP_RESULT);

  const onSubmit: SubmitHandler<Schema> = async data => {
    try {
      await updateRegularHealthCheckupResult({
        variables: convertFormDataToQueryVariables(data, personalId, resultId)
      });

      notify('更新しました', 'success');
      history.push(`/regular_health_checkup/personal_results/${personalId}/${resultId}`);
    } catch (err) {
      if (err instanceof ServerValidationError) {
        eachValidationErrorsForHookFrom(err.validationErrors, schema, data, (path, error) => {
          setError(path as FieldPath<Schema>, error);
        });
        return;
      }
      throw err;
    }
  };

  return (
    <FormProvider {...methods}>
      <Form
        personalId={personalId}
        onSubmit={handleSubmit(onSubmit)}
        onCancel={() =>
          history.push(parseBackUrl(location, `/regular_health_checkup/personal_results/${personalId}/${resultId}`))
        }
      />
    </FormProvider>
  );
};
